import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

interface Msg {
  id: number; sender_id: number; type: string; body: string | null;
  image_url: string | null; is_read: boolean; created_at: string;
  sender: { id: number; name: string; avatar: string | null } | null;
}

interface ConvInfo {
  id: number;
  other_user: { id: number; name: string; avatar: string | null; is_verified: boolean };
  ad: { id: number; title: string; price: string } | null;
}

const ChatPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [convInfo, setConvInfo] = useState<ConvInfo | null>(null);
  const [newMsg, setNewMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const hasToken = Boolean(localStorage.getItem('user'));
  const myId = JSON.parse(localStorage.getItem('user') || '{}')?.id;

  useEffect(() => {
    if (!hasToken) { navigate('/login'); return; }
    const fetch = async () => {
      try {
        setLoading(true);
        const [msgsRes, convsRes] = await Promise.all([
          api.get('/conversations/' + id + '/messages', { params: { per_page: 100 } }),
          api.get('/conversations'),
        ]);
        const msgsData = msgsRes.data.data?.data || msgsRes.data.data || [];
        const msgs = Array.isArray(msgsData) ? msgsData.reverse() : [];
        setMessages(msgs);

        const allConvs = convsRes.data.data?.data || convsRes.data.data || [];
        const thisConv = Array.isArray(allConvs) ? allConvs.find((c: any) => c.id === parseInt(id || '0')) : null;
        if (thisConv) setConvInfo(thisConv);

        await api.patch('/conversations/' + id + '/read', {}).catch(() => {});
      } catch (e) { console.error(e); } finally { setLoading(false); }
    };
    fetch();
  }, [hasToken, id, navigate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (sending) return;
    const text = newMsg.trim();
    if (!text) return;
    try {
      setSending(true);
      setNewMsg('');
      const res = await api.post('/conversations/' + id + '/messages', { type: 'text', body: text });
      const msg = res.data.data;
      setMessages(prev => [...prev, msg]);
    } catch (e) { console.error(e); setNewMsg(text); } finally { setSending(false); }
  };

  const sendImage = async (file: File) => {
    if (sending) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      alert('الصيغ المسموحة: JPG أو PNG أو WEBP'); return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('حجم الصورة يتجاوز 5 ميجابايت'); return;
    }
    try {
      setSending(true);
      const fd = new FormData();
      fd.append('type', 'image');
      fd.append('image', file);
      const res = await api.post('/conversations/' + id + '/messages', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages(prev => [...prev, res.data.data]);
    } catch (e) {
      console.error(e);
      alert('تعذّر إرسال الصورة');
    } finally {
      setSending(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const formatTime = (d: string) => {
    const date = new Date(d);
    return date.toLocaleTimeString('ar-SA', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return (
    <div style={{maxWidth:700,margin:'0 auto',padding:'60px 16px',textAlign:'center',fontFamily:"'Cairo',sans-serif"}}>
      <p style={{color:'#9E9E9E',fontSize:14}}>{'\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0645\u064a\u0644...'}</p>
    </div>
  );

  return (
    <div style={{maxWidth:700,margin:'0 auto',fontFamily:"'Cairo',sans-serif",display:'flex',flexDirection:'column',height:'calc(100vh - 80px)'}}>
      {/* Header */}
      <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',background:'#fff',borderBottom:'1px solid #E0E0E0'}}>
        <Link to="/my/conversations" style={{color:'#005343',textDecoration:'none',fontSize:20}}>{'\u2190'}</Link>
        {convInfo?.other_user && (
          <Link to={'/users/' + convInfo.other_user.id} style={{display:'flex',alignItems:'center',gap:10,textDecoration:'none',color:'inherit',flex:1}}>
            <div style={{width:40,height:40,borderRadius:'50%',background:'#005343',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,fontWeight:700,color:'#fff',overflow:'hidden'}}>
              {convInfo.other_user.avatar ? <img src={convInfo.other_user.avatar} alt="" style={{width:'100%',height:'100%',objectFit:'cover'}} /> : convInfo.other_user.name.charAt(0)}
            </div>
            <div>
              <div style={{display:'flex',alignItems:'center',gap:4}}>
                <span style={{fontSize:14,fontWeight:700,color:'#212121'}}>{convInfo.other_user.name}</span>
                {convInfo.other_user.is_verified && <span style={{fontSize:10,color:'#2196F3'}}>{'\\u2713'}</span>}
              </div>
              {convInfo.ad && <p style={{fontSize:11,color:'#005343',margin:0}}>{convInfo.ad.title}</p>}
            </div>
          </Link>
        )}
      </div>

      {/* Messages */}
      <div style={{flex:1,overflowY:'auto',padding:'16px',background:'#F5F7FA',display:'flex',flexDirection:'column',gap:8}}>
        {messages.map(m => {
          const isMine = m.sender_id === myId;
          return (
            <div key={m.id} style={{display:'flex',justifyContent:isMine?'flex-start':'flex-end'}}>
              <div style={{
                maxWidth:'75%',padding:'10px 14px',borderRadius:isMine?'16px 16px 4px 16px':'16px 16px 16px 4px',
                background:isMine?'#005343':'#fff',color:isMine?'#fff':'#212121',
                boxShadow:'0 1px 2px rgba(0,0,0,0.08)',
              }}>
                {m.type === 'image' && m.image_url && (
                  <img src={m.image_url} alt="" style={{maxWidth:'100%',borderRadius:8,marginBottom:6}} />
                )}
                {m.body && <p style={{margin:0,fontSize:14,lineHeight:1.7}}>{m.body}</p>}
                <div style={{display:'flex',justifyContent:'flex-end',alignItems:'center',gap:4,marginTop:4}}>
                  <span style={{fontSize:10,opacity:0.6}}>{formatTime(m.created_at)}</span>
                  {isMine && <span style={{fontSize:10,opacity:0.6}}>{m.is_read ? '\u2713\u2713' : '\u2713'}</span>}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{padding:'12px 16px',background:'#fff',borderTop:'1px solid #E0E0E0',display:'flex',gap:10,alignItems:'flex-end'}}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{display:'none'}}
          onChange={e => { const f = e.target.files?.[0]; if (f) sendImage(f); }}
        />
        <button onClick={() => fileInputRef.current?.click()} disabled={sending} title={'إرسال صورة'} style={{
          background:'#F0F0F0',color:'#005343',border:'none',width:44,height:44,borderRadius:'50%',
          cursor:sending?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:20,opacity:sending?0.6:1,flexShrink:0,
        }}>{'📎'}</button>
        <textarea
          value={newMsg}
          onChange={e => setNewMsg(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={'\u0627\u0643\u062a\u0628 \u0631\u0633\u0627\u0644\u0629...'}
          rows={1}
          style={{flex:1,border:'1px solid #E0E0E0',borderRadius:12,padding:'10px 14px',fontSize:14,fontFamily:"'Cairo',sans-serif",resize:'none',outline:'none',maxHeight:100}}
        />
        <button onClick={sendMessage} disabled={sending} style={{
          background:'#005343',color:'#fff',border:'none',width:44,height:44,borderRadius:'50%',
          cursor:sending?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',
          fontSize:18,opacity:sending?0.6:1,flexShrink:0,
        }}>{'\u27A4'}</button>
      </div>
    </div>
  );
};

export default ChatPage;
